/**
 * Created by neo on 18.01.17.
 */
import { makeAutoObservable, reaction, IReactionDisposer } from 'mobx';
import { PageResult } from '../PageResult';
import { HttpBackend } from '../../Services/Http/HttpBackend';
import { ExerciseVariation } from '../ProgramPortfolio/ExerciseVariation';
import { Athlete } from '../Athlete/Athlete';
import * as mobxUtils from 'mobx-utils';
import { v4 as UUID } from 'uuid';
import { BaseTrackingKey } from '../ProgramPortfolio/TrackingKeys';
import { WorkoutLogHeader, createWorkoutLogHeader } from './WorkoutLogHeader';
import SentryService from '../../Services/SentryService';
import { EventEntry } from '../Coach/Schedule/EventEntry';
import { Media } from '../Media/Media';
import CacheService from '../../Services/CacheService';
import { HOURS_24 } from '../../Utils/Constants';
import { ExerciseBlockLog, createExerciseBlockLog } from './ExerciseBlockLog';
import type { ExerciseSet } from './ExerciseSet';
import type { ViewSet } from './ViewSet';
import { MicroCycleDay } from '../Coach/Program/MicroCycleDay';
import { CoachWorkout } from '../Coach/Program/Workout/CoachWorkout';
import { createPageResult } from '../PageResult';

export type WorkoutLog = WorkoutLogHeader & {
  athlete: Athlete;
  exerciseBlocks: ExerciseBlockLog[];
  eventEntry?: EventEntry;
  elapsedTime: number;
  elapsedDisposer?: IReactionDisposer;
  readonly elapsedBreakTime: number;
  readonly sets: ExerciseSet[];
  readonly duration: number;
  readonly totalDuration: number;
  readonly isPaused: boolean;
  readonly totalPauseDuration: number;
  readonly lastPauseEntryStartTimestamp: number;
  readonly breakTime: number;
  readonly durationInSeconds: number;
  readonly blockCount: number;
  readonly lastBlock: ExerciseBlockLog | undefined;
  readonly firstSet: ExerciseSet | undefined;
  readonly lastSet: ExerciseSet | undefined;
  readonly currentSet: ExerciseSet | undefined;
  readonly lastFinishedSet: ExerciseSet | undefined;
  readonly firstStartTimestamp: number;
  readonly lastEndTimestamp: number;
  readonly totalTimeExercising: number;
  readonly totalCaloriesBurnt: number;
  readonly tons: number;
  readonly totalSets: number;
  readonly started: boolean;
  readonly ended: boolean;
  readonly active: boolean;
  readonly isRunning: boolean;
  readonly isBreak: boolean;
  readonly aggregatedValues: { [key: string]: number };
  readonly media: Media | undefined;
  getName(lang: string): string | undefined;
  start(): boolean;
  end(): WorkoutLog;
  terminateAndSave(): Promise<WorkoutLog>;
  stopExercise(save?: boolean): ExerciseSet | undefined;
  logExercise(exercise: ExerciseVariation, phaseId?: string, exerciseBlockId?: string): ExerciseBlockLog;
  totalOf(setValueKey: BaseTrackingKey): number;
  save(): Promise<WorkoutLog>;
  remove(): Promise<WorkoutLog>;
  deleteSet(viewSet: ViewSet): void;
  toHeader(): WorkoutLogHeader;
  startElapsedTimeInterval(): void;
  stopElapsedTimeInterval(): void;
};

export function createWorkoutLog(json?: Partial<WorkoutLog>): WorkoutLog {
  const defaultAthlete: Athlete = { id: '', createdAt: '', updatedAt: '', user: { id: '', email: '', roles: [], createdAt: '', updatedAt: '' }, gender: 'UNKNOWN', tags: [], allowsDataSharing: false, acceptsMarketing: false };
  const athlete: Athlete = json?.athlete ? (json.athlete as Athlete) : defaultAthlete;

  const result: WorkoutLog = makeAutoObservable({
    // WorkoutLogHeader fields
    id: json?.id ?? UUID(),
    athleteId: json?.athleteId ?? athlete.id,
    type: json?.type ?? 'gym_strength',
    startDateTime: json?.startDateTime ? new Date(json.startDateTime as any) : undefined,
    endDateTime: json?.endDateTime ? new Date(json.endDateTime as any) : undefined,
    bmr: json?.bmr ?? 1800, // TODO: Get from athlete body values
    calories: json?.calories ?? 0,
    workout: json?.workout,
    workoutId: json?.workoutId ?? json?.workout?.id,
    microCycleDay: json?.microCycleDay,
    coachProgramId: json?.coachProgramId ?? json?.microCycleDay?.athleteSuperMacroId,
    mesoCycleId: json?.mesoCycleId ?? json?.microCycleDay?.mesoCycleId,
    microCycleId: json?.microCycleId ?? json?.microCycleDay?.microCycleId,
    microCycleDayId: json?.microCycleDayId ?? json?.microCycleDay?.id,

    // WorkoutLog specific fields
    athlete,
    exerciseBlocks: [] as ExerciseBlockLog[],
    eventEntry: json?.eventEntry,
    elapsedTime: 0,
    elapsedDisposer: undefined as IReactionDisposer | undefined,

    setMicroCycleDay(day: MicroCycleDay) {
      this.microCycleDay = day;
    },

    setWorkout(workout: CoachWorkout) {
      this.workout = workout;
    },

    startElapsedTimeInterval() {
      this.elapsedDisposer?.();
      this.elapsedDisposer = reaction(
        () => mobxUtils.now(40),
        (timestamp) => {
          this.elapsedTime = timestamp - (this.startDateTime?.valueOf() ?? Date.now());
        },
        { name: 'WorkoutLog::elapsedTime' },
      );
    },

    stopElapsedTimeInterval() {
      this.elapsedDisposer?.();
      this.elapsedDisposer = undefined;
    },

    getName(lang: string): string | undefined {
      return this.workout?.getName(lang);
    },

    start(): boolean {
      if (!this.started) {
        this.startDateTime = new Date();
        return true;
      }
      return false;
    },

    end(): WorkoutLog {
      if (this.active) {
        this.stopExercise(false);
        this.endDateTime = new Date();
      }
      return this;
    },

    terminateAndSave(): Promise<WorkoutLog> {
      this.end();
      SentryService.addBreadcrumb('WorkoutLogFinished', { workoutLogId: this.id, endDateTime: this.endDateTime });
      if (this.microCycleDay) {
        this.microCycleDay.workoutLog = this.toHeader();
      }
      return this.save().then((log) => {
        return this.microCycleDay?.complete(log).then(() => log) ?? log;
      });
    },

    stopExercise(save: boolean = true): ExerciseSet | undefined {
      this.lastSet?.stop(undefined, new Date(), save);
      return this.lastSet;
    },

    logExercise(exercise: ExerciseVariation, phaseId?: string, exerciseBlockId?: string): ExerciseBlockLog {
      const existingBlock = exerciseBlockId ? this.exerciseBlocks.find((b) => b.exerciseBlockId === exerciseBlockId) : undefined;
      if (existingBlock) return existingBlock;
      if (this.lastBlock && !exerciseBlockId && this.lastBlock.exercise.id === exercise.id) return this.lastBlock;
      const newBlock = createExerciseBlockLog(this, { exercise, phaseId, exerciseBlockId } as any);
      this.exerciseBlocks.push(newBlock);
      return newBlock;
    },

    totalOf(setValueKey: BaseTrackingKey): number {
      return this.exerciseBlocks.reduce((result, block) => result + block.totalOf(setValueKey), 0);
    },

    save(): Promise<WorkoutLog> {
      const sets = this.exerciseBlocks
        .reduce((result, block) => result.concat(block.sets.slice()), [] as ExerciseSet[])
        .map((s) => ({
          setId: s.setId,
          workoutLogId: s.workoutLogId,
          exerciseId: s.exerciseBlockLog.exerciseId ?? s.exercise.exerciseIdentifier,
          met: s.exercise.met ?? 0,
          phaseId: s.exerciseBlockLog.phaseId,
          exerciseBlockId: s.exerciseBlockLog.exerciseBlockId,
          startTimestamp: s.startDateTime?.valueOf() ?? 0,
          endTimestamp: s.endDateTime?.valueOf() ?? 0,
          startDateTime: s.startDateTime?.toISOString() ?? null,
          endDateTime: s.endDateTime?.toISOString() ?? null,
          values: Object.fromEntries(s.values.entries()),
          caloriesBurnt: s.caloriesBurnt,
          pauses: s.pauses.map((p) => ({
            startDateTime: p.startDateTime instanceof Date ? p.startDateTime.toISOString() : p.startDateTime,
            endDateTime: p.endDateTime instanceof Date ? p.endDateTime.toISOString() : p.endDateTime ?? null,
          })),
        }));
      const json = {
        id: this.id,
        type: this.workout?.type,
        workoutId: this.workoutId,
        coachProgramId: this.coachProgramId,
        mesoCycleId: this.mesoCycleId,
        microCycleId: this.microCycleId,
        microCycleDayId: this.microCycleDayId,
        startTimestamp: this.startDateTime?.valueOf() ?? 0,
        endTimestamp: this.endDateTime?.valueOf() ?? 0,
        sets,
      };
      console.info('[WorkoutLog] save() payload', JSON.stringify(json, null, 2).substring(0, 2000));
      return HttpBackend.post('/activity/workoutlog/save', json).then(() => this);
    },

    remove(): Promise<WorkoutLog> {
      return HttpBackend.delete(`/activity/workoutlog/${this.id}`).then(() => {
        return this;
      });
    },

    deleteSet(viewSet: ViewSet) {
      const index = this.exerciseBlocks.findIndex((eb) => eb.exerciseBlockId === viewSet.viewBlock?.block.exerciseBlockId);
      if (index !== -1) {
        this.exerciseBlocks[index].removeSet(viewSet);
        this.save();
      }
    },

    toHeader(): WorkoutLogHeader {
      return createWorkoutLogHeader({
        id: this.id,
        athleteId: this.athlete.id,
        bmr: this.bmr, // Use the WorkoutLog's bmr field
        calories: this.calories,
        startDateTime: this.startDateTime,
        endDateTime: this.endDateTime,
        type: this.type,
      });
    },

    get elapsedBreakTime(): number {
      return this.lastSet?.breakTime ?? 0;
    },

    get sets(): ExerciseSet[] {
      return this.exerciseBlocks
        .reduce((arr, b) => arr.concat(b.sets), [] as ExerciseSet[])
        .slice()
        .sort((a, b) => !a.startDateTime ? 1 : (a.startDateTime?.valueOf() ?? Number.MAX_SAFE_INTEGER) - (b.startDateTime?.valueOf() ?? Number.MAX_SAFE_INTEGER));
    },

    get duration(): number {
      if (this.active) return this.elapsedTime;
      if (this.startDateTime) {
        return this.endDateTime
          ? this.endDateTime.valueOf() - (this.startDateTime?.valueOf() ?? this.endDateTime.valueOf())
          : Date.now() - (this.startDateTime?.valueOf() ?? Date.now());
      }
      return 0;
    },

    get totalDuration(): number {
      if (this.active) {
        if (this.isPaused) return Date.now() - (this.startDateTime?.valueOf() ?? Date.now()) - this.totalPauseDuration;
        return this.elapsedTime - this.totalPauseDuration;
      }
      if (this.startDateTime) return this.sets.reduce((value, s) => s.totalDuration + value, 0);
      return 0;
    },

    get isPaused(): boolean {
      return this.exerciseBlocks.some((eb) => eb.isPaused);
    },

    get totalPauseDuration(): number {
      if (this.active) return this.sets.reduce((value, s) => s.totalPauseDuration + value, 0);
      return 0;
    },

    get lastPauseEntryStartTimestamp(): number {
      return this.lastSet?.lastPauseEntryStartTimestamp ?? 0;
    },

    get breakTime(): number {
      if (this.startDateTime) {
        return this.sets.reduce((value, set, index, self) => (index < self.length - 1 ? set.breakTime : 0) + value, 0);
      }
      return 0;
    },

    get durationInSeconds(): number {
      return this.duration / 1000;
    },

    get blockCount(): number {
      return this.exerciseBlocks.length;
    },

    get lastBlock(): ExerciseBlockLog | undefined {
      return this.exerciseBlocks.length > 0 ? this.exerciseBlocks[this.exerciseBlocks.length - 1] : undefined;
    },

    get firstSet(): ExerciseSet | undefined {
      return this.sets.length > 0 ? this.sets[0] : undefined;
    },

    get lastSet(): ExerciseSet | undefined {
      return this.sets.length > 0 ? this.sets[this.sets.length - 1] : undefined;
    },

    get currentSet(): ExerciseSet | undefined {
      for (let i = this.sets.length - 1; i >= 0; i--) {
        if (this.sets[i].isRunning || this.sets[i].isFinished) return this.sets[i];
      }
      return undefined;
    },

    get lastFinishedSet(): ExerciseSet | undefined {
      for (let i = this.sets.length - 1; i >= 0; i--) {
        if (this.sets[i].isFinished) return this.sets[i];
      }
      return undefined;
    },

    get firstStartTimestamp(): number {
      return this.firstSet?.startDateTime?.valueOf() ?? 0;
    },

    get lastEndTimestamp(): number {
      return this.lastSet?.endDateTime?.valueOf() ?? 0;
    },

    get totalTimeExercising(): number {
      return this.sets.reduce((result, set) => result + set.duration, 0);
    },

    get totalCaloriesBurnt(): number {
      const bmr24 = this.bmr / 24.0; // Use WorkoutLog's bmr field
      return this.sets.reduce((result, set) => set.calculateCalories(bmr24) + result, 0.0);
    },

    get tons(): number {
      return this.exerciseBlocks.reduce((result, block) => result + block.tons, 0);
    },

    get totalSets(): number {
      return this.exerciseBlocks.reduce((result, block) => result + block.sets.length, 0);
    },

    get started(): boolean {
      return !!this.startDateTime;
    },

    get ended(): boolean {
      return !!this.endDateTime;
    },

    get active(): boolean {
      return this.started && !this.ended;
    },

    get isRunning(): boolean {
      if (this.active) return this.lastSet?.isRunning ?? false;
      return false;
    },

    get isBreak(): boolean {
      if (this.active) return this.lastSet?.isFinished ?? false;
      return false;
    },

    get aggregatedValues(): { [key: string]: number } {
      return this.sets.reduce(
        (acc, set) => {
          Array.from(set.values.entries())
            .filter(([key]) => key !== 'DURATION' && key !== 'CALORIES')
            .forEach(([key, value]) => {
              acc[key] = (acc[key] ?? 0) + value;
            });
          return acc;
        },
        { CALORIES: this.totalCaloriesBurnt, DURATION: this.totalDuration / 1000.0 } as { [key: string]: number },
      );
    },

    get media(): Media | undefined {
      return this.workout?.image;
    },
  });

  // Initialize exerciseBlocks
  if (json?.exerciseBlocks) {
    result.exerciseBlocks = (json.exerciseBlocks as any[]).map((v) => createExerciseBlockLog(result, v));
  }

  return result;
}

export const WorkoutLogApi = {
  get(workoutLogId: string): Promise<WorkoutLog | undefined> {
    return CacheService.cache(
      `workoutLog:${workoutLogId}`,
      () => HttpBackend.get(`/activity/workoutlog/${workoutLogId}`, { withAthlete: false, withWorkout: false }),
      (res) => createWorkoutLog(res),
      HOURS_24,
    ).then((res) => (res ? createWorkoutLog(res as any) : undefined));
  },

  async find(params?: any): Promise<PageResult<WorkoutLog>> {
    const data = await HttpBackend.get('/activity/workoutlog', params);
    if (data?.content) {
      data.content = data.content.map((w: any) => createWorkoutLog(w));
      return createPageResult(data);
    }
    return createPageResult();
  },

  current(athlete?: Athlete): Promise<WorkoutLog | undefined> {
    return HttpBackend.get('/activity/workoutlog/current')
      .then((data) => (data ? createWorkoutLog(data) : undefined))
      .catch((e) => {
        SentryService.captureException(e);
        return undefined;
      });
  },
};
